/**
 * ImageCropper - Fullscreen crop UI for dice face images
 * Uses react-easy-crop with square 1:1 aspect ratio for consistent dice training data
 */

import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper, { type Area } from 'react-easy-crop';
import { Button } from '@/components/ui/button';

/**
 * Props for the ImageCropper component
 */
interface ImageCropperProps {
  /** Source image URL to crop */
  imageUrl: string;
  /** Callback with the cropped image blob */
  onCropComplete: (blob: Blob) => void;
  /** Callback to cancel cropping */
  onCancel: () => void;
}

/**
 * Crop an image to the specified pixel area using canvas
 *
 * @param imageUrl - Source image URL
 * @param pixelCrop - Pixel coordinates and dimensions to crop
 * @returns Cropped image as a JPEG Blob
 */
async function cropImage(imageUrl: string, pixelCrop: Area): Promise<Blob> {
  const image = new Image();
  image.crossOrigin = 'anonymous';

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = reject;
    image.src = imageUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas 2D context');
  }

  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y,
    pixelCrop.width, pixelCrop.height,
    0, 0,
    pixelCrop.width, pixelCrop.height
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
      'image/jpeg',
      0.9
    );
  });
}

/**
 * Fullscreen image cropper with zoom control and apply/cancel actions.
 * Renders as a fixed overlay at z-[60] to sit above drawer content.
 */
export function ImageCropper({ imageUrl, onCropComplete, onCancel }: ImageCropperProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [cropError, setCropError] = useState<string | null>(null);

  /** Store the crop area in pixels when the user adjusts the crop region */
  const handleCropComplete = useCallback((_croppedArea: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  /** Apply the crop: render to canvas, convert to blob, and pass to parent */
  const handleApply = useCallback(async () => {
    if (!croppedAreaPixels) return;

    setIsApplying(true);
    setCropError(null);
    try {
      const blob = await cropImage(imageUrl, croppedAreaPixels);
      onCropComplete(blob);
    } catch (error) {
      console.warn('[IMAGE_CROPPER] Crop failed', error);
      setCropError('Failed to crop image. Please try again.');
    } finally {
      setIsApplying(false);
    }
  }, [croppedAreaPixels, imageUrl, onCropComplete]);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-black">
      {/* Crop area */}
      <div className="relative flex-1">
        <Cropper
          image={imageUrl}
          crop={crop}
          zoom={zoom}
          aspect={1}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={handleCropComplete}
        />
      </div>

      {/* Controls */}
      <div className="p-4 space-y-3 bg-black/90">
        {/* Zoom slider */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-white/70">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-primary"
            aria-label="Zoom level"
          />
        </div>

        {/* Error message */}
        {cropError && (
          <p className="text-sm text-destructive text-center" role="alert">
            {cropError}
          </p>
        )}

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onCancel}
            disabled={isApplying}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            className="flex-1"
            onClick={handleApply}
            disabled={!croppedAreaPixels || isApplying}
          >
            {isApplying ? 'Applying...' : 'Apply Crop'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ImageCropper;
